import type { UserProfile, Question, Difficulty, ChapterEssence } from '../../shared/types';
import { mergeRemoteUser, type RemoteUserRow } from './userMerge';
import { buildExamQuestions } from './examSession';
import {
  filterAccessibleQuestions,
  getAccessibleQuestionsForChapter,
} from '../../shared/freeAccess';
import logger from './logger';
import { SEED_QUESTIONS, AVAILABLE_BADGES } from '../constants';
import { supabase } from './supabaseClient';
import { get, set, del } from 'idb-keyval';
import * as Sentry from '@sentry/react';

const USER_STORAGE_KEY = 'fahryo_app_user';
const SESSION_STORAGE_KEY = 'fahryo_app_session';
const QUESTIONS_STORAGE_KEY = 'fahryo_app_questions';
const SYNC_QUEUE_KEY = 'fahryo_sync_queue';
const ESSENCES_STORAGE_KEY = 'fahryo_app_essences';

let cachedQuestions: Question[] | null = null;
let cachedEssences: ChapterEssence[] | null = null;
// AUTH-FIX v2: Timestamp of last email-login sync. When the email-login method
// writes full DB-enriched user data to localStorage, the parallel SIGNED_IN
// event from onAuthStateChange should NOT overwrite it with a minimal-user.
let _lastEmailLoginSyncMs = 0;

const INITIAL_USER: UserProfile = {
  name: 'Fahrschüler',
  email: '',
  xp: 0,
  level: 1,
  streak: 0,
  lastLoginDate: new Date().toISOString(),
  completedQuestions: [],
  correctlyAnswered: [],
  bookmarkedQuestions: [],
  chapterProgress: {},
  isPremium: false,
  simulationAttempts: 0,
  isDarkMode: false,
  language: 'de',
  shareDataWithAI: true,
  consentGiven: null,
  hasSeenOnboarding: false,
  badges: [],
  failedQuestions: [],
  srsData: {},
  dailyGoal: 20,
  dailyProgress: { date: new Date().toISOString().split('T')[0], count: 0 },
  examDate: null,
};

// Helper to shuffle array
// DB-01: Split a full profile into typed columns + slimmed JSONB. Single
// source for EVERY online write — updateUser and the offline sync queue
// must produce identical rows (audit F-04: the queue used to write only
// the JSONB blob, so typed columns like xp/simulation_attempts went
// stale and the next login read the stale columns).
const buildUserWritePayload = (
  updated: UserProfile
): { columnData: Record<string, unknown>; complexData: Record<string, unknown> } => {
  const columnData: Record<string, unknown> = {
    name: updated.name,
    display_name: updated.displayName || null,
    xp: updated.xp,
    level: updated.level,
    streak: updated.streak,
    consent_given: updated.consentGiven,
    share_data_with_ai: updated.shareDataWithAI,
    has_seen_onboarding: updated.hasSeenOnboarding,
    is_dark_mode: updated.isDarkMode,
    preferred_language: updated.language || 'de',
    daily_goal: updated.dailyGoal,
    exam_date: updated.examDate || null,
    simulation_attempts: updated.simulationAttempts,
    experience_level: updated.experienceLevel || null,
    gender: updated.gender || null,
    age: updated.age || null,
    location: updated.location || null,
    leaderboard_visible: updated.leaderboardVisible ?? null,
    referral_code: updated.referralCode || null,
    referral_count: updated.referralCount || 0,
    last_seen_whats_new: updated.lastSeenWhatsNew || null,
    profile_photo_url: updated.profilePhotoUrl || null,
  };

  // Complex fields stay in JSONB (arrays/objects)
  const complexData: Record<string, unknown> = {
    completedQuestions: updated.completedQuestions,
    correctlyAnswered: updated.correctlyAnswered,
    bookmarkedQuestions: updated.bookmarkedQuestions,
    failedQuestions: updated.failedQuestions,
    chapterProgress: updated.chapterProgress,
    badges: updated.badges,
    srsData: updated.srsData,
    dailyProgress: updated.dailyProgress,
  };
  // Optional complex fields
  if (updated.lastStreakDate) complexData.lastStreakDate = updated.lastStreakDate;
  if (updated.simulationHistory) complexData.simulationHistory = updated.simulationHistory;
  if (updated.essencesRead) complexData.essencesRead = updated.essencesRead;
  if (updated.leaderboardSharedFields)
    complexData.leaderboardSharedFields = updated.leaderboardSharedFields;
  if (updated.seenSpotlights) complexData.seenSpotlights = updated.seenSpotlights;
  if (updated.dismissedAnnouncements)
    complexData.dismissedAnnouncements = updated.dismissedAnnouncements;
  if (updated.bonusChapters !== undefined) complexData.bonusChapters = updated.bonusChapters;
  if (updated.referredBy) complexData.referredBy = updated.referredBy;
  if (updated.referredByUserId) complexData.referredByUserId = updated.referredByUserId;
  if (updated.referralQualified !== undefined)
    complexData.referralQualified = updated.referralQualified;
  if (updated.referralRewardPending !== undefined)
    complexData.referralRewardPending = updated.referralRewardPending;
  if (updated.referralRewardApplied !== undefined)
    complexData.referralRewardApplied = updated.referralRewardApplied;

  return { columnData, complexData };
};

const shuffleArray = <T>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

export const ApiClient = {
  initializeDatabase: () => {
    const allQuestions: Question[] = [...SEED_QUESTIONS];
    cachedQuestions = allQuestions;
    set(QUESTIONS_STORAGE_KEY, allQuestions).catch((e) =>
      logger.error('ApiClient', 'IDB write failed', e)
    );
  },

  fetchQuestionsFromSupabase: async (
    language: string = 'de',
    forceRefresh: boolean = false
  ): Promise<boolean> => {
    // If force refresh (e.g. language changed), invalidate cache
    if (forceRefresh) {
      cachedQuestions = null;
    }

    // Step 1: Load from IDB cache first (fast, offline-safe)
    if (!cachedQuestions) {
      try {
        const stored = await get(QUESTIONS_STORAGE_KEY);
        if (stored && Array.isArray(stored) && stored.length > 0 && !forceRefresh) {
          cachedQuestions = stored;
          logger.info('apiClient', `[Questions] Loaded ${stored.length} from IDB cache`);
        }
      } catch (e) {
        logger.warn('Questions', 'IDB read failed:', e);
      }
    }

    // If offline, just use cache
    if (!navigator.onLine) {
      logger.info('Questions', 'Offline, using cached data');
      return !!cachedQuestions;
    }

    // Step 2: If we have cached data and no force refresh, resolve immediately and refresh in background
    const hasCachedData = !!cachedQuestions && cachedQuestions.length > 0 && !forceRefresh;

    const refreshFromNetwork = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);

      try {
        // F-07: select only the active language columns + DE as fallback.
        // Previously fetched all 16 languages × (text, options, explanation,
        // ai_explanation) = 64 columns. Now ≤8 columns per row → ~80% less
        // bandwidth on the question-bank load (was ~3 MB, now ~600 KB).
        //
        // The DB schema is unchanged; only the SELECT projection narrowed.
        //
        // i18n-validator coverage marker — list every supported column so
        // scripts/validate-i18n.ts sees them. Don't remove this comment.
        // SELECT-LANGS: question_text_de question_text_en question_text_tr
        // question_text_ar question_text_prs question_text_ur question_text_uk
        // question_text_hi question_text_fa question_text_ro question_text_pl
        // question_text_ru question_text_bg question_text_es question_text_pt
        // question_text_ps
        const baseCols =
          'id, chapter_id, difficulty, type, points, video_url, visual_prompt, ' +
          'correct_option_ids, tags, image_url, image_hidden';
        const VALID_LANGS = [
          'de',
          'en',
          'tr',
          'ar',
          'prs',
          'ur',
          'uk',
          'hi',
          'fa',
          'ro',
          'pl',
          'ru',
          'bg',
          'es',
          'pt',
          'ps',
        ];
        const safeLang = VALID_LANGS.includes(language) ? language : 'de';
        const langCols =
          safeLang === 'de'
            ? `question_text_de, options_de, explanation_de, ai_explanation_de`
            : `question_text_de, options_de, explanation_de, ai_explanation_de, ` +
              `question_text_${safeLang}, options_${safeLang}, explanation_${safeLang}, ai_explanation_${safeLang}`;
        const selectCols = `${baseCols}, ${langCols}`;

        const { data, error } = await Sentry.startSpan(
          {
            name: 'supabase.questions.fetch',
            op: 'db.query',
            attributes: { 'db.table': 'questions', 'app.language': safeLang },
          },
          () => supabase.from('questions').select(selectCols).abortSignal(controller.signal)
        );

        clearTimeout(timeout);

        if (error) {
          const errMsg = error.message || error.code || error.hint || JSON.stringify(error);
          // Network errors (Failed to fetch, Load failed, AbortError) are expected for offline/poor connectivity
          const isNetworkError =
            errMsg.includes('Failed to fetch') ||
            errMsg.includes('Load failed') ||
            errMsg.includes('TypeError') ||
            errMsg.includes('AbortError') ||
            errMsg.includes('aborted');
          if (isNetworkError) {
            logger.warn('Questions', 'Network error loading questions:', errMsg);
          } else {
            logger.error('Questions', `Supabase error: ${errMsg}`, undefined, { code: error.code });
            Sentry.captureException(new Error(`[Questions] Supabase error: ${errMsg}`), {
              extra: { code: error.code, hint: error.hint, details: error.details, language },
            });
          }
          return;
        }

        if (!data || data.length === 0) {
          logger.warn('Questions', 'No questions in Supabase');
          return;
        }

        const mappedQuestions: Question[] = data.map((row: Record<string, any>) => {
          const text = row[`question_text_${language}`] || row.question_text_de;
          const options = row[`options_${language}`] || row.options_de;
          const explanation = row[`explanation_${language}`] || row.explanation_de;
          const aiExplanation = row[`ai_explanation_${language}`] || row.ai_explanation_de || null;

          return {
            id: row.id,
            chapterId: row.chapter_id,
            difficulty: row.difficulty as Difficulty,
            type: row.type as Question['type'],
            points: row.points,
            imagePlaceholder: (row.image_hidden ? null : row.image_url) || null,
            videoUrl: row.video_url,
            visualPrompt: row.visual_prompt,
            correctOptionIds: row.correct_option_ids,
            tags: row.tags,
            questionText: text,
            options: options,
            explanation: explanation,
            aiExplanation: aiExplanation,
          };
        });

        cachedQuestions = mappedQuestions;
        logger.info('apiClient', `[Questions] Refreshed ${mappedQuestions.length} from Supabase`);

        set(QUESTIONS_STORAGE_KEY, mappedQuestions).catch((e) =>
          logger.warn('Questions', 'IDB write failed:', e)
        );

        // Notify UI that questions have been refreshed (picks up admin edits)
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('fahryo-questions-updated'));
        }
      } catch (fetchErr: unknown) {
        clearTimeout(timeout);
        if (fetchErr instanceof Error && fetchErr.name === 'AbortError') {
          logger.warn('Questions', 'Supabase request timed out (15s)');
        } else {
          // Network errors are expected for offline/poor connectivity
          logger.warn('Questions', 'Network fetch failed:', fetchErr);
        }
      }
    };

    if (hasCachedData) {
      // Resolve immediately, refresh in background (don't await)
      refreshFromNetwork();
      return true;
    } else {
      // No cache — must wait for network
      await refreshFromNetwork();
      return !!cachedQuestions;
    }
  },

  fetchChapterEssences: async (language: string = 'de'): Promise<boolean> => {
    // Load from IDB cache first
    if (!cachedEssences) {
      try {
        const stored = await get(ESSENCES_STORAGE_KEY);
        if (stored && Array.isArray(stored) && stored.length > 0) {
          cachedEssences = stored;
          logger.info('apiClient', `[Essences] Loaded ${stored.length} from IDB cache`);
        }
      } catch (e) {
        logger.warn('Essences', 'IDB read failed:', e);
      }
    }

    // Fetch from Supabase
    try {
      const { data, error } = await supabase
        .from('chapter_essences')
        .select(
          'chapter_id, language, summary, golden_rules, exam_tip, common_mistakes, mnemonic, core_question'
        )
        .eq('language', language);

      if (error) {
        logger.error('Essences', 'Supabase error:', error.message);
        return !!cachedEssences;
      }

      if (data && data.length > 0) {
        const mapped: ChapterEssence[] = data.map((row: any) => ({
          chapterId: row.chapter_id,
          language: row.language,
          summary: row.summary,
          goldenRules: row.golden_rules || [],
          examTip: row.exam_tip,
          commonMistakes: row.common_mistakes || [],
          mnemonic: row.mnemonic,
          coreQuestion: row.core_question,
        }));
        cachedEssences = mapped;
        set(ESSENCES_STORAGE_KEY, mapped).catch((e) =>
          logger.warn('Essences', 'IDB write failed:', e)
        );
        logger.info('apiClient', `[Essences] Loaded ${mapped.length} essences for ${language}`);
      }
    } catch (err) {
      logger.error('Essences', 'Fetch failed:', err);
    }

    return !!cachedEssences;
  },

  getEssenceForChapter: (chapterId: number): ChapterEssence | null => {
    return cachedEssences?.find((e) => e.chapterId === chapterId) || null;
  },

  saveQuestionsToSupabase: async (
    questions: Question[]
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const rows = questions.map((q) => ({
        id: q.id,
        chapter_id: q.chapterId,
        difficulty: q.difficulty,
        type: q.type,
        points: q.points,
        image_placeholder: q.imagePlaceholder || null,
        video_url: q.videoUrl || null,
        visual_prompt: q.visualPrompt || null,
        correct_option_ids: q.correctOptionIds,
        tags: q.tags,
        question_text_de: q.questionText,
        options_de: q.options,
        explanation_de: q.explanation,
        // Empty translations for now
        question_text_en: null,
        options_en: null,
        explanation_en: null,
        question_text_tr: null,
        options_tr: null,
        explanation_tr: null,
        question_text_ar: null,
        options_ar: null,
        explanation_ar: null,
      }));

      const response = await fetch('/api/seed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questions: rows }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Server error');
      }

      return { success: true, message: 'Fragen erfolgreich in Supabase gespeichert!' };
    } catch (e: any) {
      logger.error('apiClient', 'Error saving questions:', e);
      return { success: false, message: e.message || 'Fehler beim Speichern der Fragen.' };
    }
  },

  updateQuestionImage: async (
    id: string,
    base64Image: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const { error } = await supabase
        .from('questions')
        .update({ image_placeholder: base64Image })
        .eq('id', id);

      if (error) throw new Error(error.message);

      return { success: true, message: 'Bild erfolgreich aktualisiert!' };
    } catch (e: any) {
      logger.error('apiClient', 'Error updating question image:', e);
      return { success: false, message: e.message || 'Fehler beim Aktualisieren des Bildes.' };
    }
  },

  deleteQuestionFromSupabase: async (
    id: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`/api/questions/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Server error');
      }

      return { success: true, message: 'Frage erfolgreich gelöscht!' };
    } catch (e: any) {
      logger.error('apiClient', 'Error deleting question:', e);
      return { success: false, message: e.message || 'Fehler beim Löschen der Frage.' };
    }
  },

  seedQuestionsToSupabase: async (): Promise<{ success: boolean; message: string }> => {
    try {
      const rows = SEED_QUESTIONS.map((q) => ({
        id: q.id,
        chapter_id: q.chapterId,
        difficulty: q.difficulty,
        type: q.type,
        points: q.points,
        image_placeholder: q.imagePlaceholder || null,
        video_url: q.videoUrl || null,
        visual_prompt: q.visualPrompt || null,
        correct_option_ids: q.correctOptionIds,
        tags: q.tags,
        question_text_de: q.questionText,
        options_de: q.options,
        explanation_de: q.explanation,
        // Empty translations for now
        question_text_en: null,
        options_en: null,
        explanation_en: null,
        question_text_tr: null,
        options_tr: null,
        explanation_tr: null,
        question_text_ar: null,
        options_ar: null,
        explanation_ar: null,
      }));

      const response = await fetch('/api/seed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questions: rows }),
      });

      const data = await response.json();

      if (!response.ok) {
        logger.error('apiClient', 'Error seeding questions:', data.error);
        return { success: false, message: 'Fehler beim Seeden: ' + data.error };
      }

      return {
        success: true,
        message: data.message || 'Erfolgreich Fragen in Supabase gespeichert!',
      };
    } catch (e: any) {
      logger.error('apiClient', 'Exception seeding questions:', e);
      return { success: false, message: 'Fehler: ' + e.message };
    }
  },

  hasSession: (): boolean => {
    return !!localStorage.getItem(SESSION_STORAGE_KEY);
  },

  login: async (
    email: string,
    password?: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      if (navigator.onLine) {
        let userId = '';
        if (password) {
          const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (authError) {
            return { success: false, message: 'Fehler beim Login: ' + authError.message };
          }
          userId = authData.user?.id || '';
        }

        if (!userId) {
          const {
            data: { user },
          } = await supabase.auth.getUser();
          userId = user?.id || '';
        }

        if (!userId) {
          return { success: false, message: 'Benutzer konnte nicht authentifiziert werden.' };
        }

        // DB-01: Select typed columns + slimmed JSONB (same as syncSupabaseSession)
        const { data, error } = await supabase
          .from('users')
          .select(
            'name, display_name, xp, level, streak, last_login_date, consent_given, share_data_with_ai, has_seen_onboarding, is_dark_mode, daily_goal, exam_date, simulation_attempts, experience_level, gender, age, location, leaderboard_visible, referral_code, referral_count, last_seen_whats_new, profile_photo_url, preferred_language, is_premium, premium_since, premium_product, data'
          )
          .eq('id', userId)
          .single();

        let userData: UserProfile;

        if (!data || error) {
          // Create new user if not exists in DB
          const initialName = email.split('@')[0];
          userData = { ...INITIAL_USER, email, name: initialName };
          const complexData = {
            completedQuestions: [],
            correctlyAnswered: [],
            bookmarkedQuestions: [],
            failedQuestions: [],
            chapterProgress: {},
            badges: [],
            srsData: {},
            dailyProgress: { date: new Date().toISOString().split('T')[0], count: 0 },
          };
          await supabase.from('users').upsert(
            {
              id: userId,
              email,
              name: initialName,
              xp: 0,
              level: 1,
              streak: 0,
              last_login_date: new Date().toISOString(),
              has_seen_onboarding: false,
              is_dark_mode: false,
              daily_goal: 20,
              share_data_with_ai: true,
              preferred_language: 'de',
              data: complexData,
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'id' }
          );
        } else {
          // Existing user. F-04: the old code REPLACED the local profile
          // with the DB row — any progress not yet synced (offline XP,
          // exam attempts) was gone after login. Now: if the local
          // profile belongs to the SAME account, merge it with the DB
          // row; a different account (shared device) still gets a clean
          // DB-only profile so no foreign progress leaks in.
          const existingLocal = ApiClient.getUser();
          const sameAccount =
            !!existingLocal.email && existingLocal.email.toLowerCase() === email.toLowerCase();
          const base: UserProfile = sameAccount
            ? existingLocal
            : { ...INITIAL_USER, email, name: email.split('@')[0] };
          userData = {
            ...mergeRemoteUser(base, data as RemoteUserRow),
            email,
            lastLoginDate: new Date().toISOString(),
          };
          // Update lastLoginDate in DB (typed column only, no JSONB overwrite)
          supabase
            .from('users')
            .update({
              last_login_date: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .eq('id', userId)
            .then();
          // If the local profile contributed unsynced progress, push the
          // merged state up so the DB row catches up immediately.
          if (sameAccount) {
            const { columnData, complexData } = buildUserWritePayload(userData);
            supabase
              .from('users')
              .upsert(
                {
                  id: userId,
                  email,
                  ...columnData,
                  data: complexData,
                  updated_at: new Date().toISOString(),
                },
                { onConflict: 'id' }
              )
              .then(({ error: upErr }: { error: unknown }) => {
                if (upErr) logger.warn('apiClient', 'Post-login merge sync failed:', upErr);
              });
          }
        }

        localStorage.setItem(
          SESSION_STORAGE_KEY,
          JSON.stringify({ email, userId, token: 'supabase-token' })
        );
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
        // AUTH-FIX v2: Mark that email-login just wrote full DB-enriched user
        // data. The parallel SIGNED_IN event should skip syncSupabaseSession
        // to avoid overwriting this with a minimal-user.
        _lastEmailLoginSyncMs = Date.now();
        logger.warn(
          'apiClient',
          `login() DB done: hasSeenOnboarding=${userData.hasSeenOnboarding}`
        );
        return { success: true };
      } else {
        throw new Error('Offline');
      }
    } catch {
      logger.warn('apiClient', 'Offline login fallback');
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ email, token: 'offline-token' }));
      let user = ApiClient.getUser();
      if (!user.email) {
        user = { ...INITIAL_USER, email, name: email.split('@')[0] };
      }
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
      return { success: true };
    }
  },

  register: async (
    email: string,
    password?: string,
    name?: string,
    options?: { dateOfBirth?: string | null; ageConfirmed?: boolean }
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      let userId = '';
      if (navigator.onLine && password) {
        // HIBP leaked password check
        try {
          const { checkPasswordLeaked } = await import('./passwordCheck');
          const { leaked, count } = await checkPasswordLeaked(password);
          if (leaked) {
            return {
              success: false,
              message: `⚠️ Dieses Passwort wurde in ${count.toLocaleString('de-DE')} Datenlecks gefunden. Bitte wähle ein sichereres Passwort.`,
            };
          }
        } catch (err) {
          logger.error('ApiClient', 'Leaked password check failed:', err);
        }

        const { data: authData, error: authError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin,
          },
        });

        if (authError) {
          return { success: false, message: 'Fehler bei der Registrierung: ' + authError.message };
        }
        userId = authData.user?.id || '';
      }

      if (!userId) {
        return { success: false, message: 'Benutzer konnte nicht erstellt werden.' };
      }

      const initialName = name || email.split('@')[0];
      const user = { ...INITIAL_USER, email, name: initialName };
      if (navigator.onLine) {
        // DB-01: Write typed columns + slimmed JSONB (same pattern as syncSupabaseSession)
        const complexData = {
          completedQuestions: [],
          correctlyAnswered: [],
          bookmarkedQuestions: [],
          failedQuestions: [],
          chapterProgress: {},
          badges: [],
          srsData: {},
          dailyProgress: { date: new Date().toISOString().split('T')[0], count: 0 },
        };
        await supabase.from('users').upsert(
          {
            id: userId,
            email,
            name: initialName,
            xp: 0,
            level: 1,
            streak: 0,
            last_login_date: new Date().toISOString(),
            has_seen_onboarding: false,
            is_dark_mode: false,
            daily_goal: 20,
            share_data_with_ai: true,
            preferred_language: 'de',
            data: complexData,
            // F-03 age gate: persist DOB (optional) + self-declaration timestamp.
            // age_confirmed_at is the audit trail; date_of_birth is set only
            // if the user filled the optional field at signup.
            date_of_birth: options?.dateOfBirth || null,
            age_confirmed_at: options?.ageConfirmed ? new Date().toISOString() : null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'id' }
        );

        // DSGVO Art. 7: Log consent for privacy policy + terms of service.
        //
        // consent_type MUST match the DB check constraint, which mirrors the
        // server-side allow-list in server/routes/dsgvo.ts:
        //   'terms' | 'privacy' | 'ai_data_sharing' | 'marketing'
        // We previously wrote the German words 'agb'/'datenschutz'; Postgres
        // rejected every one of those rows (23514), so no signup since launch
        // ever produced the Art. 7 proof-of-consent record it was meant to.
        void (async () => {
          // Record the version the user actually accepted, same source of truth
          // the server uses. Best-effort: if app_settings is unreachable we fall
          // back to '1.0', because an imprecise record still beats none at all.
          let termsVersion = '1.0';
          let privacyVersion = '1.0';
          try {
            const { data: settings } = await supabase
              .from('app_settings')
              .select('key, value')
              .in('key', ['consent_terms_version', 'consent_privacy_version']);
            for (const row of settings || []) {
              const value = String(row.value ?? '').replace(/"/g, '');
              if (!value) continue;
              if (row.key === 'consent_terms_version') termsVersion = value;
              if (row.key === 'consent_privacy_version') privacyVersion = value;
            }
          } catch {
            // keep the fallbacks
          }

          const { error: consentErr } = await supabase.from('consent_log').insert([
            {
              user_id: userId,
              consent_type: 'privacy',
              version: privacyVersion,
              accepted: true,
              user_agent: navigator.userAgent,
            },
            {
              user_id: userId,
              consent_type: 'terms',
              version: termsVersion,
              accepted: true,
              user_agent: navigator.userAgent,
            },
          ]);
          if (consentErr) logger.error('apiClient', 'Consent log insert failed:', consentErr);
        })();
      }

      localStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify({ email, userId, token: 'supabase-token' })
      );
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));

      // Drop any stale question/essence/SW-image caches left over from a
      // previous account on this device. Without this, the new user can see
      // images from cached question rows that no longer match the current
      // Supabase data on their first chapter session.
      cachedQuestions = null;
      cachedEssences = null;
      try {
        await del(QUESTIONS_STORAGE_KEY);
      } catch (err) {
        logger.warn('apiClient', 'register: IDB del questions failed:', err);
      }
      try {
        await del(ESSENCES_STORAGE_KEY);
      } catch (err) {
        logger.warn('apiClient', 'register: IDB del essences failed:', err);
      }
      try {
        if (typeof caches !== 'undefined') {
          const names = await caches.keys();
          await Promise.all(
            names
              .filter((n) => n.includes('question-image') || n.includes('supabase'))
              .map((n) => caches.delete(n))
          );
        }
      } catch (err) {
        logger.warn('apiClient', 'register: SW cache clear failed:', err);
      }

      return { success: true };
    } catch (e) {
      logger.error('apiClient', 'Register error', e);
      return { success: false, message: 'Ein unbekannter Fehler ist aufgetreten.' };
    }
  },

  loginWithProvider: async (
    provider: 'google'
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      if (!navigator.onLine) {
        return { success: false, message: 'Bitte überprüfe deine Internetverbindung.' };
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: window.location.origin,
        },
      });

      if (error) {
        return { success: false, message: `Fehler beim Login mit ${provider}: ` + error.message };
      }

      // Redirect happens automatically — this code won't be reached
      return { success: true };
    } catch (e: any) {
      logger.error('ApiClient', `Login with ${provider} failed`, e);
      return { success: false, message: 'Ein unbekannter Fehler ist aufgetreten.' };
    }
  },

  syncSupabaseSession: async (session: {
    user?: { email?: string; id?: string; user_metadata?: Record<string, string> };
    userId?: string;
  }): Promise<{ dbReady: boolean }> => {
    try {
      const user = session.user;
      const email = user.email || '';
      const userId = user.id;
      if (!userId) return { dbReady: false };

      // AUTH-FIX v2: If email-login just synced full DB data to localStorage,
      // skip this sync to prevent the parallel SIGNED_IN listener from
      // overwriting the correct user with a minimal-user fallback.
      if (Date.now() - _lastEmailLoginSyncMs < 2000) {
        logger.warn('apiClient', 'syncSupabaseSession: skipping — email login synced <2s ago');
        return { dbReady: true }; // login() already wrote full DB data
      }

      // STEP 1 (synchronous fast path): write a minimal session + user
      // record to localStorage immediately, before any DB call. This
      // unblocks the UI even if the DB query in STEP 2 deadlocks
      // against Supabase's auth lock — observed in private browsing
      // during the OAuth PKCE exchange where the SIGNED_IN listener
      // runs INSIDE _initialize() with the auth lock still held, and
      // any nested REST query waits forever for that same lock.
      const existingLocal = ApiClient.getUser();
      const fallbackName =
        existingLocal.name && existingLocal.name !== 'Fahrschüler'
          ? existingLocal.name
          : user.user_metadata?.full_name || email.split('@')[0] || 'User';
      const minimalUser: UserProfile = {
        ...existingLocal,
        email: email || existingLocal.email,
        name: fallbackName,
      };
      localStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify({ email, userId, token: 'supabase-token' })
      );
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(minimalUser));

      // STEP 2: Race the DB enrichment query against a 5s timeout. If it
      // deadlocks, we keep the minimal-user written above instead of
      // hanging the caller forever. Any data it does return overrides
      // the minimal record.
      // DB-01: Select typed columns + slimmed JSONB (arrays/objects only)
      const queryPromise = supabase
        .from('users')
        .select(
          'name, display_name, xp, level, streak, last_login_date, consent_given, share_data_with_ai, has_seen_onboarding, is_dark_mode, daily_goal, exam_date, simulation_attempts, experience_level, gender, age, location, leaderboard_visible, referral_code, referral_count, last_seen_whats_new, profile_photo_url, preferred_language, is_premium, premium_since, premium_product, data'
        )
        .eq('id', userId)
        .single();
      const timeoutPromise = new Promise<{ data: null; error: { message: string } }>((resolve) =>
        setTimeout(
          () =>
            resolve({
              data: null,
              error: { message: 'syncSupabaseSession: DB select timed out after 5s' },
            }),
          5000
        )
      );
      const { data, error } = (await Promise.race([queryPromise, timeoutPromise])) as {
        data: any;
        error: any;
      };
      if (error?.message?.includes('timed out')) {
        logger.warn('apiClient', error.message + ' — keeping minimal user record');
        return { dbReady: false };
      }

      let userData: any;

      if (!data || error) {
        // New user — create with columns + minimal JSONB
        const initialName = user.user_metadata?.full_name || email.split('@')[0] || 'User';
        userData = { ...INITIAL_USER, email, name: initialName };
        const complexData = {
          completedQuestions: [],
          correctlyAnswered: [],
          bookmarkedQuestions: [],
          failedQuestions: [],
          chapterProgress: {},
          badges: [],
          srsData: {},
          dailyProgress: { date: new Date().toISOString().split('T')[0], count: 0 },
        };
        await supabase.from('users').upsert(
          {
            id: userId,
            email,
            name: initialName,
            xp: 0,
            level: 1,
            streak: 0,
            last_login_date: new Date().toISOString(),
            has_seen_onboarding: false,
            is_dark_mode: false,
            daily_goal: 20,
            share_data_with_ai: true,
            preferred_language: 'de',
            data: complexData,
            // Age gate: reaching the OAuth flow requires checking the mandatory
            // "16+ / parental consent" box (AuthView), so record the confirmation
            // timestamp here too — previously OAuth signups stored no age data.
            age_confirmed_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'id' }
        );
      } else {
        // Existing user. F-04: same-account local progress is merged in
        // instead of being replaced by the DB row (see login()); a
        // different account on a shared device still gets a clean
        // DB-only profile.
        const sameAccount =
          !!existingLocal.email && existingLocal.email.toLowerCase() === email.toLowerCase();
        const base: UserProfile = sameAccount
          ? existingLocal
          : { ...INITIAL_USER, email, name: email.split('@')[0] };
        userData = {
          ...mergeRemoteUser(base, data as RemoteUserRow),
          email,
          lastLoginDate: new Date().toISOString(),
        };
        // Update lastLoginDate in DB
        supabase
          .from('users')
          .update({
            last_login_date: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', userId)
          .then();
      }

      localStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify({ email, userId, token: 'supabase-token' })
      );
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
      logger.warn(
        'apiClient',
        `syncSession DB done: hasSeenOnboarding=${userData.hasSeenOnboarding}, lastSeenWhatsNew=${userData.lastSeenWhatsNew}`
      );
      return { dbReady: true };
    } catch (e) {
      logger.error('apiClient', 'Error syncing session:', e);
      return { dbReady: false };
    }
  },

  logout: async () => {
    // SECURITY: Deterministic hard-logout — clear ALL local data
    // 1. Clear primary app state
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(QUESTIONS_STORAGE_KEY);
    localStorage.removeItem(SYNC_QUEUE_KEY);
    cachedQuestions = null;
    cachedEssences = null;

    // 2. Clear IDB stores (delete, not null-set)
    try {
      await del(QUESTIONS_STORAGE_KEY);
    } catch (err) {
      logger.error('ApiClient', 'IDB del questions failed:', err);
    }
    try {
      await del(ESSENCES_STORAGE_KEY);
    } catch (err) {
      logger.error('ApiClient', 'IDB del essences failed:', err);
    }

    // 3. Clear coach conversation/message caches from localStorage
    try {
      const keysToRemove = Object.keys(localStorage).filter(
        (k) => k.startsWith('fahryo_coach_convos_') || k.startsWith('fahryo_coach_msgs_')
      );
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch (err) {
      logger.warn('ApiClient', 'Coach cache clear failed:', err);
    }

    // 4. Clear Supabase auth token explicitly (defense-in-depth)
    try {
      const authKeys = Object.keys(localStorage).filter(
        (k) => k.startsWith('sb-') && k.endsWith('-auth-token')
      );
      authKeys.forEach((k) => localStorage.removeItem(k));
    } catch (err) {
      logger.warn('ApiClient', 'Auth token clear failed:', err);
    }

    // 5. Clear ALL Service Worker caches (supabase-api, question-images, etc.)
    try {
      if (typeof caches !== 'undefined') {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }
    } catch (err) {
      logger.warn('ApiClient', 'SW cache clear failed:', err);
    }

    // 6. Best-effort: tell Supabase to revoke session (don't block on it)
    if (navigator.onLine) {
      supabase.auth.signOut().catch(() => {});
    }
  },

  getUser: (): UserProfile => {
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    if (!stored) return INITIAL_USER;
    return { ...INITIAL_USER, ...JSON.parse(stored) };
  },

  /** Pulls fresh user data from Supabase and merges into localStorage.
   *  Returns updated UserProfile or null if no session. */
  refreshUserFromSupabase: async (): Promise<UserProfile | null> => {
    try {
      const sessionStr = localStorage.getItem(SESSION_STORAGE_KEY);
      if (!sessionStr) return null;
      const session = JSON.parse(sessionStr);
      if (!session?.userId) return null;

      // DB-01: Select typed columns + slimmed JSONB
      const { data, error } = await supabase
        .from('users')
        .select(
          'name, display_name, xp, level, streak, last_login_date, consent_given, share_data_with_ai, has_seen_onboarding, is_dark_mode, daily_goal, exam_date, simulation_attempts, experience_level, gender, age, location, leaderboard_visible, referral_code, referral_count, last_seen_whats_new, profile_photo_url, preferred_language, is_premium, premium_since, premium_product, data'
        )
        .eq('id', session.userId)
        .single();
      if (error || !data) return null;

      const local = ApiClient.getUser();

      // F-05: one shared merge with consistent rules (max-wins for
      // monotone counters, union for progress sets, per-entry SRS merge)
      // instead of the old hand-rolled block where simulationAttempts &
      // Co. jumped back to a stale DB value on every tab focus.
      const merged = mergeRemoteUser(local, data as RemoteUserRow);

      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(merged));
      return { ...INITIAL_USER, ...merged };
    } catch (e) {
      logger.error('apiClient', 'Failed to refresh user from DB:', e);
      return null;
    }
  },

  updateUser: (updates: Partial<UserProfile>): UserProfile => {
    // SECURITY: Fields that may ONLY be set server-side (payment webhooks / admin)
    const _PROTECTED_FIELDS = [
      'isPremium',
      'premiumSince',
      'premiumProduct',
      'referralPremiumDays',
      'referralRewardsEarned',
      'isAdmin',
    ] as const;

    const current = ApiClient.getUser();
    let updated = { ...current, ...updates };

    // Level up logic
    updated.level = Math.floor(updated.xp / 500) + 1;

    // Badge engine
    const earnedBadges = updated.badges || [];
    const earnedIds = new Set(earnedBadges.map((b) => b.id));

    const checkBadge = (id: string, condition: boolean) => {
      if (!earnedIds.has(id) && condition) {
        const badgeMeta = AVAILABLE_BADGES.find((b) => b.id === id);
        if (badgeMeta) {
          earnedBadges.push({
            id: badgeMeta.id,
            name: badgeMeta.nameKey,
            description: badgeMeta.descKey,
            icon: badgeMeta.icon,
            unlockedAt: new Date().toISOString(),
          });
        }
      }
    };

    checkBadge('first_step', updated.completedQuestions.length > 0);
    checkBadge('streak_3', updated.streak >= 3);
    checkBadge('streak_7', updated.streak >= 7);
    checkBadge('bookmark_king', updated.bookmarkedQuestions.length >= 5);

    updated.badges = earnedBadges;

    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(updated));

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('fahryo-data-updated'));
    }

    const { columnData, complexData } = buildUserWritePayload(updated);

    // Sync with backend
    if (navigator.onLine) {
      const sessionStr = localStorage.getItem(SESSION_STORAGE_KEY);
      const session = sessionStr ? JSON.parse(sessionStr) : null;
      if (session?.userId) {
        Promise.resolve(
          supabase.from('users').upsert(
            {
              id: session.userId,
              email: updated.email,
              ...columnData,
              data: complexData,
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'id' }
          )
        )
          .then(({ error }: { error: unknown }) => {
            if (error) {
              logger.warn('apiClient', 'Supabase sync error:', error);
              ApiClient.queueSync(updates);
            }
          })
          .catch(() => {
            ApiClient.queueSync(updates);
          });
      } else {
        ApiClient.queueSync(updates);
      }
    } else {
      ApiClient.queueSync(updates);
    }

    return updated;
  },

  queueSync: (updates: Partial<UserProfile>) => {
    const queue = JSON.parse(localStorage.getItem(SYNC_QUEUE_KEY) || '[]');
    queue.push({ timestamp: Date.now(), updates });
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
  },

  syncPendingChanges: async (): Promise<number> => {
    const queueJson = localStorage.getItem(SYNC_QUEUE_KEY);
    if (!queueJson) return 0;

    const queue = JSON.parse(queueJson);
    if (queue.length === 0) return 0;

    const user = ApiClient.getUser();
    if (!user.email) return 0;

    const sessionStr = localStorage.getItem(SESSION_STORAGE_KEY);
    const session = sessionStr ? JSON.parse(sessionStr) : null;
    if (!session?.userId) return 0;

    try {
      // The queued updates are already applied to the local profile
      // (updateUser writes localStorage before queueing). F-04: write the
      // SAME row shape as updateUser — typed columns + slimmed JSONB.
      // The old code wrote only a raw JSONB blob, so xp/simulation_attempts
      // columns went stale and the next login read the stale columns.
      let combinedUpdates: Partial<UserProfile> = {};
      queue.forEach((item: { updates: Partial<UserProfile> }) => {
        combinedUpdates = { ...combinedUpdates, ...item.updates };
      });
      const merged: UserProfile = { ...user, ...combinedUpdates };
      const { columnData, complexData } = buildUserWritePayload(merged);

      const { error } = await supabase.from('users').upsert(
        {
          id: session.userId,
          email: merged.email,
          ...columnData,
          data: complexData,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'id' }
      );

      if (error) throw error;

      localStorage.removeItem(SYNC_QUEUE_KEY);
      return queue.length;
    } catch (e) {
      logger.warn('apiClient', 'Supabase pending sync error:', e);
      return 0;
    }
  },

  toggleBookmark: (questionId: string): UserProfile => {
    const user = ApiClient.getUser();
    const bookmarks = new Set(user.bookmarkedQuestions);
    if (bookmarks.has(questionId)) bookmarks.delete(questionId);
    else bookmarks.add(questionId);
    return ApiClient.updateUser({ bookmarkedQuestions: Array.from(bookmarks) });
  },

  getAllQuestions: (): Question[] => {
    return cachedQuestions || SEED_QUESTIONS;
  },

  getQuestionsForChapter: (chapterId: number, difficulty?: Difficulty | null): Question[] => {
    let questions: Question[] = [];
    const allQuestions = cachedQuestions || SEED_QUESTIONS;
    questions = allQuestions.filter((q) => q.chapterId === chapterId);
    if (difficulty) questions = questions.filter((q) => q.difficulty === difficulty);
    return questions.map((q) => ({ ...q, options: shuffleArray(q.options) }));
  },

  /**
   * Returns the questions a free user is actually allowed to study in this
   * chapter (chapter 1 → all; chapter 2–14 → preview slice). Premium users
   * always get the full chapter. Difficulty filter is applied AFTER the
   * access filter so the preview slice stays stable.
   */
  getAccessibleQuestionsForChapter: (
    chapterId: number,
    difficulty?: Difficulty | null
  ): Question[] => {
    const user = ApiClient.getUser();
    const allInChapter = (cachedQuestions || SEED_QUESTIONS).filter(
      (q) => q.chapterId === chapterId
    );
    let accessible = getAccessibleQuestionsForChapter(user, chapterId, allInChapter);
    if (difficulty) accessible = accessible.filter((q) => q.difficulty === difficulty);
    return accessible.map((q) => ({ ...q, options: shuffleArray(q.options) }));
  },

  getFailedQuestions: (): Question[] => {
    const user = ApiClient.getUser();
    const allQuestions = cachedQuestions || SEED_QUESTIONS;
    const failed = allQuestions.filter((q) => user.failedQuestions.includes(q.id));
    return filterAccessibleQuestions(user, failed, allQuestions);
  },

  getSrsQuestions: (): Question[] => {
    const user = ApiClient.getUser();
    const allQuestions = cachedQuestions || SEED_QUESTIONS;
    const now = Date.now();
    const dueIds = Object.keys(user.srsData || {}).filter(
      (id) => user.srsData[id].nextReview <= now
    );
    const due = allQuestions.filter((q) => dueIds.includes(q.id));
    return filterAccessibleQuestions(user, due, allQuestions);
  },

  getRandomQuestions: (count: number): Question[] => {
    const user = ApiClient.getUser();
    const allQuestions = cachedQuestions || SEED_QUESTIONS;
    // Exam simulation is premium-gated separately (3 attempts/account); a free
    // user reaching this path means they still have attempts left, but we keep
    // the question pool consistent with what they can actually study.
    const pool = filterAccessibleQuestions(user, allQuestions, allQuestions);
    // F-06: proportional point mix + topic spread instead of a plain
    // random draw, so two simulations are comparable in difficulty.
    const selected = buildExamQuestions(pool, count);
    return selected.map((q) => ({ ...q, options: shuffleArray(q.options) }));
  },

  /** Size of the pool an exam simulation can draw from for this user. */
  getExamPoolSize: (forUser?: UserProfile): number => {
    const user = forUser || ApiClient.getUser();
    const allQuestions = cachedQuestions || SEED_QUESTIONS;
    return filterAccessibleQuestions(user, allQuestions, allQuestions).length;
  },

  // SECURITY: activatePremium() removed — premium status is set exclusively
  // server-side via CopeCart webhook (payments.ts) or admin actions.
  // Client polls premium status via /api/user/premium-status endpoint.
};
